import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const CareerSection = () => {
  const opportunities = [
    {
      icon: TrendingUp,
      title: "Join a Fast-Paced Team",
      description: "Join our team at Terces and work in a fast-paced environment where you can learn and grow. We're committed to investing in our employees and providing opportunities for career advancement.",
      link: "/Terces_Recruitment"
    },
    {
      icon: Users,
      title: "Make a Difference",
      description: "At Terces, we believe in making a positive impact on our clients, our community, and the world. Join us and be a part of a team that's dedicated to making a difference through our work.",
      link: "/Terces_Recruitment"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      x: -50,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 1.1 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section className="relative py-24 md:py-32 px-6 bg-white overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-20 right-10 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-yellow-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20 md:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Join Our Team
            </span>
          </motion.div>

          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-[1.1] mb-6"
            style={{ letterSpacing: '-0.02em' }}
          >
            <span className="text-gray-900">Build Your </span>
            <span className="relative inline-block">
              <span className="relative z-10">Career</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>{" "}
            <br />
            <span className="text-gray-900">With Terces</span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Discover exciting opportunities to grow and make an impact in the restaurant consulting industry
          </motion.p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Side - Image */}
          <motion.div
            variants={imageVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              {/* Placeholder for your career image */}
              <div className="aspect-[4/5] bg-gradient-to-br from-primary/10 to-yellow-400/10">
                <img 
                  src="https://images.unsplash.com/photo-1552566626-52f8b828add9?w=800&h=1000&fit=crop"
                  alt="Restaurant team"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                viewport={{ once: true }}
                className="absolute bottom-6 left-6 right-6 bg-white rounded-2xl shadow-xl p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-primary">4+</div>
                    <div className="text-sm text-gray-600 mt-1">Years of Excellence</div>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div>
                    <div className="text-3xl font-bold text-primary">10+</div>
                    <div className="text-sm text-gray-600 mt-1">Team Members</div>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div>
                    <div className="text-3xl font-bold text-primary">15+</div>
                    <div className="text-sm text-gray-600 mt-1">Projects Delivered</div>
                  </div>
                </div>
              </motion.div>

              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-bl-full"></div>
            </div>

            {/* Decorative Element */}
            <motion.div
              animate={{
                rotate: [0, 180, 360],
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-8 -left-8 w-24 h-24 border-4 border-primary opacity-20 z-0"
              style={{ transform: 'rotate(45deg)' }}
            />
          </motion.div>

          {/* Right Side - Opportunities */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-8 order-1 lg:order-2"
          >
            {opportunities.map((opportunity, index) => {
              const Icon = opportunity.icon;
              
              return (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ 
                    x: 10,
                    transition: { duration: 0.3 }
                  }}
                  className="group relative bg-[#F6F4EF] rounded-3xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-500"
                >
                  {/* Gradient overlay on hover */}
                  <motion.div
                    className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(27, 120, 64, 0.05) 0%, transparent 100%)' 
                    }}
                  />

                  <div className="relative">
                    {/* Icon */}
                    <motion.div
                      whileHover={{ 
                        scale: 1.1,
                        rotate: [0, -10, 10, 0],
                        transition: { duration: 0.5 }
                      }}
                      className="inline-block mb-6"
                    >
                      <div className="w-16 h-16 rounded-2xl bg-white border-2 border-primary flex items-center justify-center relative overflow-hidden">
                        {/* Shine effect */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.6 }}
                        />
                        <Icon 
                          className="w-8 h-8 text-primary relative z-10"
                          strokeWidth={1.5}
                        />
                      </div>
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight"
                      style={{ letterSpacing: '-0.01em' }}
                    >
                      {opportunity.title}
                    </h3>

                    {/* Description */}
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                      {opportunity.description}
                    </p>

                    {/* CTA Button */}
                    <Link to={opportunity.link}>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="group/btn inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative"
                      >
                        <span className="relative z-10 flex items-center gap-3">
                          <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                          Join Us
                        </span>
                        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></span>
                      </motion.button>
                    </Link>
                  </div>

                  {/* Bottom accent line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-primary rounded-b-3xl"
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-20 md:mt-24"
        >
          <motion.p
            className="text-gray-600 text-lg mb-8"
          >
            Ready to start your journey with us?
          </motion.p>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/Terces_Recruitment"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white hover:bg-gray-50 text-primary border-2 border-primary rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
            >
              View All Openings
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CareerSection;